/***
 * This file is part of Olvid Web.
 * Copyright (C) 2021 Lise Jolicoeur, Jérémie Martel
 * 
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 ***/
import {handleConnectionToServerLost, handleProtocolError} from "@/assets/ext/error";
import {registerConnection} from "@/assets/ext/messages/messageSender"; //registerCorresponding
import {globals} from "@/assets/ext/globals";
import {serverMessageHandler} from "@/assets/ext/messages/serverMessageHandler";
import {saveDraft} from '@/assets/ext/draft';

export let websocket = {
    connection: null,
    timeout : null,

    connect: function(serverUrl) {
        if ((this.connection != null && this.connection.readyState === this.connection.OPEN)
            || (this.connection != null && this.connection.readyState === this.connection.CONNECTING)) { 
            console.log("Trying to connect while a connection is already open !");
            return ;
        }

        this.connection = new WebSocket(serverUrl);
        this.connection.onopen = () => {
            if (globals.connectionIdentifier) {
                registerConnection(globals.connectionIdentifier);

                // open another websocket connection to server to send fake messages to trigger lambda functions
                console.log("Opening fake connection to wake server up ....")
                let fakeWebsocketConnection = new WebSocket(serverUrl);
                let responseCount = 0;
                fakeWebsocketConnection.onopen = () => {
                    const fakeRegisterCorrespondingMessage = JSON.stringify({action: "registerCorresponding"});
                    const fakeRegisterConnectionMessage = JSON.stringify({action: "registerCorresponding"});
                    const fakeRelayMessage = JSON.stringify({action: "relay"});
                    // create another registerConnectionInstance
                    fakeWebsocketConnection.send(fakeRegisterConnectionMessage)
                    // create two registerCorresponding
                    fakeWebsocketConnection.send(fakeRegisterCorrespondingMessage)
                    fakeWebsocketConnection.send(fakeRegisterCorrespondingMessage)
                    // create five relay functions
                    fakeWebsocketConnection.send(fakeRelayMessage)
                    fakeWebsocketConnection.send(fakeRelayMessage)
                    fakeWebsocketConnection.send(fakeRelayMessage)
                    fakeWebsocketConnection.send(fakeRelayMessage)
                    fakeWebsocketConnection.send(fakeRelayMessage)
                    fakeWebsocketConnection.send(fakeRelayMessage)
                }
                fakeWebsocketConnection.onmessage = () => {
                    responseCount += 1;
                    if (responseCount === 9) {
                        console.log("Closing fake connection")
                        fakeWebsocketConnection.close();
                    }
                }
            }

            if (globals.status === globals.STATUS_DISCONNECTED) {
                globals.updateStatus(globals.STATUS_CONNECTED_TO_SERVER);
            }
            else if (globals.status === globals.STATUS_RECONNECTING) {
                // registerConnection response will call registerCorresponding, and response will update Stratus to ready
                console.log("successfulReconnectionHandler: reconnection successful")
                if(this.timeout){
                    clearTimeout(this.timeout);
                }
            }
            else {
                console.warn("Opened websocket connection from an invalid status");
            }
        }
        this.connection.onclose = () => {
            switch (globals.status) {
                case (globals.STATUS_DISCONNECTED):
                case (globals.STATUS_CONNECTED_TO_SERVER):
                case (globals.STATUS_READY):
                case (globals.STATUS_RECONNECTING):
                    this.reconnect(globals.serverUrl);
                    saveDraft(globals.currentDiscussion);
                    break ;
                case (globals.STATUS_CLOSING_SESSION):
                    console.log("Normally closed websocket connection");
                    break ;
                case (globals.STATUS_PROTOCOL_IN_PROGRESS):
                    // do not try to reconnect if protocol was not finished
                    handleProtocolError();
                    break ;
                case (globals.STATUS_WAITING_FOR_RECONNECTION):
                    console.log("Lost connection to server ");
                    handleConnectionToServerLost();
                    break ;
                default:
                    console.log("Invalid status when websocket closed, exiting");
                    handleConnectionToServerLost();
                    break ;
            }
        };
        this.connection.onerror = function (event) {
            console.log("Websocket error found: ", event);
        }
        this.connection.onmessage = function (event) {
            try {
                serverMessageHandler(event.data);
            }
            catch (e) {
                console.log("onmessage: an exception occurred", e)
            }
        }
    },

    send: function(message) {
        if (!this.connection) {
            console.warn("No websocket connection opened, cannot send a message");
        }
        else if (this.connection.readyState !== 1) {
            console.warn("Websocket connection is in an invalid state: " + this.connection.readyState);
            // switch to reconnecting mode if readyState is closing or closed, and not already in reconnecting state
            if (this.connection.readyState === 2 || this.connection.readyState === 3) {
                if (globals.status === globals.STATUS_READY) {
                    globals.updateStatus(globals.STATUS_RECONNECTING);
                }
            }
        }
        else {
            this.connection.send(message);
        }
    },

    reconnect: function() {
       
        console.log("Trying to reconnect to websocket server")
        if (!this.connection) {
            console.log("No active connection to reconnect");
            return ;
        }
        // Simple reconnection process: on qr code page
        if (globals.status === globals.STATUS_DISCONNECTED || globals.status === globals.STATUS_CONNECTED_TO_SERVER) {
            globals.updateStatus(globals.STATUS_DISCONNECTED);
            this.connect(globals.constants.serverUrl);
        }
        // Lost connection to server during protocol
        else if (globals.status === globals.STATUS_PROTOCOL_IN_PROGRESS) {
            console.warn("Lost connection during protocol, aborting");
            handleProtocolError();
        }
        // Lost connection after protocol, try to reconnect
        else if (globals.status === globals.STATUS_READY || globals.status === globals.STATUS_RECONNECTING) {
            // if first reconnection try, set to reconnection status and launch interval to retry connecting
            if (globals.status === globals.STATUS_READY) {
                globals.updateStatus(globals.STATUS_RECONNECTING);
                this.timeout = setTimeout(() => {
                    console.log("reconnection timed out");
                    handleConnectionToServerLost();//declare server lost after 30 seconds
                }, 30_000);
            }
            this.connect(globals.constants.serverUrl);
        }
        else {
            console.warn("Reconnection failed, unexpected status");
        }
    },
};
